import { useEffect, useMemo, useState, type FC } from 'react';
import {
  Button,
  Dropdown,
  InlineNotification,
  NumberInput,
  RadioButton,
  RadioButtonGroup,
  Slider,
  Tag,
  TextInput,
} from '@carbon/react';
import { Add, TrashCan } from '@carbon/icons-react';
import { formatNumber } from 'utils/number';
import { PctInput } from './pct-input';
import {
  CARRY_TRIGGERS,
  CARRY_TRIGGER_BY_ID,
  DEFAULT_PAYOUT_BASIS,
  PAYOUT_BASES,
  carryTermsFromWells,
  splitWellsByInterest,
  thresholdForTrigger,
  triggerHasBasis,
} from './interest-modeling-carry';
import {
  DEFAULT_BONUS_PER_ACRE,
  DEFAULT_ELECTION_TRIGGER,
  ELECTION_VARIANT_BY_ID,
  LEASE_ROYALTY_MAX,
  electionGroupsOf,
  electionTermsMissing,
} from './interest-modeling-election';
import { PropertyPicker } from './property-picker';
import type { WorkingConfigForm } from './hooks';
import type { CarryWell, ElectionGroup, InterestItem } from './types';
import styles from './election-editor.module.scss';

/** The threshold slider's props (the subset used here). */
interface ThresholdSliderProps {
  id: string;
  labelText: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (data: { value: number | string }) => void;
}

/**
 * Carbon's `Slider` is typed to return `ReactNode` (includes `undefined`), which the
 * repo's React 17 JSX types reject as a JSX element. Cast to a component over the props
 * we use — the runtime component is unchanged (same cast the carry editor uses).
 */
const ThresholdSlider = Slider as unknown as FC<ThresholdSliderProps>;

const usdWhole = (value: number): string =>
  `$${formatNumber({ value, minPrecision: 0, maxPrecision: 0 })}`;

/** Props for {@link ElectionEditor}. */
export interface ElectionEditorProps {
  /** The UMI election item being edited (`kind === 'election'`). */
  item: InterestItem;
  /** The tract's valued well roster (from `useTractWells`) — the frame-scope seed. */
  wells: CarryWell[];
  /** Whether the roster fetch has RESOLVED for this tract — gates the term seed. */
  wellsResolved: boolean;
  /** The tract's net acres (from `useTractWells`) — seeds the $/NMA bonus; null ⇒ manual total. */
  netAcres: number | null | undefined;
  /** The lifted working-config form — the election setters. */
  workingConfig: WorkingConfigForm;
}

/**
 * The UMI election editor — the expanded tree-node body for a
 * `kind: 'election'` item. It CAPTURES election terms into the working config; the
 * three-variant election math is the calculator's job.
 *
 * The tract's properties are split into GROUPS, exactly as an OGL carry position is
 * (INS-10673): a group prices ONE interest, elects its own outcome, and takes its
 * properties from the same colour-coded picker. What stays per-TRACT is what is
 * negotiated once for the tract rather than once per group — the royalty rates, the
 * risk-penalty trigger, and the acreage-driven signing bonus (acreage does not
 * partition by property: a tract with 31 slots can carry a single subtract, and the
 * bonus is paid once on it). Renders:
 *
 *  - One card per group: name, property picker, participating WI + NRI (the
 *    "participate" election; also the APO terms for carried). The group cards sit
 *    INSIDE the blue box with the carried terms — the participating interest they
 *    carry is the carried election's after-payout half, so the box keeps the shape
 *    it had before grouping split those inputs onto the cards.
 *  - Carried royalty rate while carried (cap {@link CARRIED_ROYALTY_MAX}% of the owner's share).
 *  - A risk-penalty payout trigger (the shared 7-trigger catalog) + a threshold slider
 *    whose range/step/default come from the trigger; a trigger change resets the threshold.
 *  - Lease royalty rate (cap {@link LEASE_ROYALTY_MAX}%) + a lease signing bonus entered as a
 *    total, or on a $/net-mineral-acre basis when the tract's acreage resolves (acreage is
 *    read from the assets view, never fabricated — shown as "—" when absent).
 *
 * A freshly-added election arrives with no terms; once its wells resolve, the default
 * terms + all the tract's wells + the acreage-seeded bonus are seeded (idempotent, so a
 * reopened item is untouched).
 *
 * @param {ElectionEditorProps} props - The election item, its roster + acreage, and the working config.
 * @return {JSX.Element} The election editor body.
 */
export function ElectionEditor({
  item,
  wells,
  wellsResolved,
  netAcres,
  workingConfig,
}: ElectionEditorProps) {
  const { tractId } = item;
  const {
    seedElection,
    patchElectionTerms,
    addElectionGroup,
    renameElectionGroup,
    removeElectionGroup,
    setElectionGroupWells,
    patchElectionGroupTerms,
  } = workingConfig;
  const groups = useMemo(() => electionGroupsOf(item), [item]);

  // Real acreage only — null when the tract carries no figure (never fabricated).
  const acres = typeof netAcres === 'number' && netAcres > 0 ? netAcres : null;

  const [bonusMode, setBonusMode] = useState<'total' | 'perAcre'>('total');
  const [perAcre, setPerAcre] = useState(() => {
    if (acres !== null && item.leaseBonus) {
      return Math.round(item.leaseBonus / acres);
    }
    return DEFAULT_BONUS_PER_ACRE;
  });

  // Seed the default terms + all the tract's wells + the acreage-seeded bonus once the
  // roster resolves. `seedElection` is idempotent (no-op when already seeded), so a
  // reopened item is never overwritten and a double-fire is harmless.
  useEffect(() => {
    if (wellsResolved && item.wellIds === undefined) {
      seedElection(tractId, wells, netAcres);
    }
  }, [wellsResolved, item.wellIds, wells, tractId, netAcres, seedElection]);

  // Terms aren't present until the seed runs — show a hint rather than rendering
  // controls bound to undefined values.
  if (item.wellIds === undefined) {
    return (
      <div className={styles.inputs}>
        <span className={styles.loading}>Loading election…</span>
      </div>
    );
  }

  const trigger = item.trigger ?? DEFAULT_ELECTION_TRIGGER;
  const triggerMeta = CARRY_TRIGGER_BY_ID[trigger];

  const applyPerAcre = (next: number) => {
    setPerAcre(next);
    if (acres !== null) {
      patchElectionTerms(tractId, { leaseBonus: Math.round(next * acres) });
    }
  };

  const changeBonusMode = (mode: 'total' | 'perAcre') => {
    setBonusMode(mode);
    // Switching to $/acre reflects the CURRENT total as an equivalent rate rather than
    // overwriting it from a stale rate — a hand-entered total survives the mode switch;
    // editing the rate then recomputes the total.
    if (mode === 'perAcre' && acres !== null) {
      setPerAcre(Math.round((item.leaseBonus ?? 0) / acres));
    }
  };

  // The participate terms are seeded from our actual interest in the tract's wells.
  // When they could not be derived the fields are left EMPTY (INS-10673) and this
  // says why. Keyed off the item's own missing terms rather than the roster alone, so
  // it covers BOTH causes — wells that disagree, and a tract with no valued property
  // to read an interest from. (Sandbox UAT found the second: two empty boxes and
  // nothing explaining them.) Gated on the roster having resolved so the note never
  // flashes while the wells are still loading.
  const rateCount = splitWellsByInterest(wells).length;
  // A group that HOLDS properties but has no participating interest — the state a
  // group lands in when its properties carry no certified figure.
  const termsMissing = wellsResolved && electionTermsMissing(item);
  // Properties in no group at all. They are not part of the election — the preview
  // prices the union of the groups — so the count is stated rather than left for the
  // user to work out from the per-group counts.
  const assignedIds = new Set(groups.flatMap((group) => group.wellIds));
  const unassignedCount = wells.filter(
    (well) => !assignedIds.has(well.id)
  ).length;
  // A tract whose properties disagree on our interest is seeded with an EMPTY group
  // rather than a blended one, so the user has to split it. Say so — otherwise the
  // empty group reads as a bug. (Sandbox UAT found the equivalent gap on the old
  // per-tract editor: two blank boxes and nothing explaining them.) Counted, not
  // hand-waved, so the reader can see how many rates are in play.
  const mustSplit = wellsResolved && unassignedCount > 0 && rateCount > 1;
  const noProperties = wellsResolved && wells.length === 0;

  return (
    <div className={styles.inputs}>
      {noProperties ? (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title="Enter the participating interest"
          subtitle="This tract has no valued properties, so there is no interest to seed. Enter the participating WI and NRI for this election."
        />
      ) : termsMissing ? (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title="Enter the participating interest"
          subtitle="These properties carry no certified interest, so there is no figure to seed. Enter the participating WI and NRI for the group."
        />
      ) : mustSplit ? (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title="Split this tract into groups"
          subtitle={`This tract's properties carry ${rateCount} different interests, so there is no single figure to seed. Add each set of properties that shares a rate to its own group.`}
        />
      ) : null}
      {/* The two term blocks are boxed and labelled in their election's own colour,
        so the inputs read as belonging to the outcome they drive — the same blue and
        purple the chart series and the result cards use. Colour comes from the variant
        catalog (ELECTION_VARIANT_BY_ID), never a re-typed hex, so the box can never
        drift from the series it is matching. The legend carries the election's NAME
        too, so the association survives for a reader who cannot separate the hues. */}
      <fieldset
        className={styles.termsBox}
        style={{ borderColor: ELECTION_VARIANT_BY_ID.carried.color }}
      >
        <legend
          className={styles.termsBoxLegend}
          style={{ color: ELECTION_VARIANT_BY_ID.carried.color }}
        >
          {ELECTION_VARIANT_BY_ID.carried.label}
        </legend>
        <div className={styles.groups}>
          <div className={styles.groupsHead}>
            <span className={styles.sectionLabel}>
              Property groups
              {unassignedCount > 0 && ` · ${unassignedCount} unassigned`}
            </span>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Add}
              onClick={() => addElectionGroup(tractId)}
            >
              Add group
            </Button>
          </div>
          {groups.map((group) => (
            <ElectionGroupCard
              key={group.id}
              tractId={tractId}
              group={group}
              groups={groups}
              wells={wells}
              canRemove={groups.length > 1}
              renameElectionGroup={renameElectionGroup}
              removeElectionGroup={removeElectionGroup}
              setElectionGroupWells={setElectionGroupWells}
              patchElectionGroupTerms={patchElectionGroupTerms}
            />
          ))}
        </div>

        <div className={styles.field}>
          <Dropdown
            id={`${tractId}-trigger`}
            size="sm"
            titleText="Risk-penalty payout"
            label="Payout trigger"
            items={CARRY_TRIGGERS}
            itemToString={(meta) => meta?.label ?? ''}
            selectedItem={triggerMeta}
            onChange={({ selectedItem }) => {
              if (!selectedItem) {
                return;
              }
              // A trigger change snaps the threshold to that trigger's default.
              patchElectionTerms(tractId, {
                trigger: selectedItem.id,
                threshold: thresholdForTrigger(selectedItem.id),
              });
            }}
          />
        </div>
        {/* A recovery trigger states a multiple OF something; the two conventions
          differ by ~34 points of effective penalty on the same wells, so the basis
          is chosen here rather than assumed. Hidden for triggers that open no
          account — a date or a gross-accumulation trigger has nothing to measure. */}
        {triggerHasBasis(trigger) && (
          <div className={styles.field}>
            <Dropdown
              id={`${tractId}-basis`}
              size="sm"
              titleText="Payout measured on"
              label="Payout basis"
              items={PAYOUT_BASES}
              itemToString={(meta) => meta?.label ?? ''}
              selectedItem={
                PAYOUT_BASES.find(
                  (meta) =>
                    meta.id === (item.payoutBasis ?? DEFAULT_PAYOUT_BASIS)
                ) ?? PAYOUT_BASES[0]
              }
              helperText={
                PAYOUT_BASES.find(
                  (meta) =>
                    meta.id === (item.payoutBasis ?? DEFAULT_PAYOUT_BASIS)
                )?.help
              }
              onChange={({ selectedItem }) => {
                if (!selectedItem) {
                  return;
                }
                patchElectionTerms(tractId, { payoutBasis: selectedItem.id });
              }}
            />
          </div>
        )}
        {triggerMeta.hasValue && (
          <div className={styles.field}>
            <ThresholdSlider
              id={`${tractId}-threshold`}
              labelText={`Penalty / hurdle${
                triggerMeta.unit ? ` (${triggerMeta.unit})` : ''
              }`}
              min={triggerMeta.min ?? 0}
              max={triggerMeta.max ?? 100}
              step={triggerMeta.step ?? 1}
              value={item.threshold ?? 0}
              onChange={({ value }) =>
                patchElectionTerms(tractId, {
                  threshold: typeof value === 'number' ? value : Number(value),
                })
              }
            />
          </div>
        )}
      </fieldset>

      <fieldset
        className={styles.termsBox}
        style={{ borderColor: ELECTION_VARIANT_BY_ID.lease.color }}
      >
        <legend
          className={styles.termsBoxLegend}
          style={{ color: ELECTION_VARIANT_BY_ID.lease.color }}
        >
          {ELECTION_VARIANT_BY_ID.lease.label}
        </legend>
        <PctInput
          id={`${tractId}-leaseroyalty`}
          label="Lease royalty rate (% of your share)"
          max={LEASE_ROYALTY_MAX}
          value={item.leaseRoyaltyNri ?? 0}
          onChange={(value) =>
            patchElectionTerms(tractId, { leaseRoyaltyNri: value })
          }
        />

        <div className={styles.bonusGroup}>
          <span className={styles.bonusLabel}>Lease signing bonus</span>
          {acres !== null ? (
            <>
              {/* A two-option either/or is a selection — a horizontal radio group per
              Carbon usage guidance, not a ContentSwitcher. */}
              <RadioButtonGroup
                name={`${tractId}-bonus-mode`}
                orientation="horizontal"
                valueSelected={bonusMode}
                onChange={(value) =>
                  changeBonusMode(value === 'perAcre' ? 'perAcre' : 'total')
                }
              >
                <RadioButton value="total" labelText="$ total" />
                <RadioButton value="perAcre" labelText="$ / acre" />
              </RadioButtonGroup>

              {bonusMode === 'total' ? (
                <NumberInput
                  id={`${tractId}-bonus-total`}
                  label="Total bonus ($)"
                  min={0}
                  step={50_000}
                  value={item.leaseBonus ?? 0}
                  onChange={(_event, { value }) =>
                    patchElectionTerms(tractId, {
                      leaseBonus:
                        typeof value === 'number' ? value : Number(value) || 0,
                    })
                  }
                />
              ) : (
                <>
                  <NumberInput
                    id={`${tractId}-bonus-peracre`}
                    label="Bonus ($ / net mineral acre)"
                    min={0}
                    step={100}
                    value={perAcre}
                    onChange={(_event, { value }) =>
                      applyPerAcre(
                        typeof value === 'number' ? value : Number(value) || 0
                      )
                    }
                  />
                  <span className={styles.bonusHint}>
                    {formatNumber({
                      value: acres,
                      minPrecision: 0,
                      maxPrecision: 2,
                    })}{' '}
                    net mineral acres · total {usdWhole(item.leaseBonus ?? 0)}
                  </span>
                </>
              )}
            </>
          ) : (
            <>
              <NumberInput
                id={`${tractId}-bonus-total`}
                label="Total bonus ($)"
                min={0}
                step={50_000}
                value={item.leaseBonus ?? 0}
                onChange={(_event, { value }) =>
                  patchElectionTerms(tractId, {
                    leaseBonus:
                      typeof value === 'number' ? value : Number(value) || 0,
                  })
                }
              />
              {/* No acreage on this tract — the $/acre basis is unavailable, so the
              bonus is a manual total and the acreage reads "—" (never fabricated). */}
              <span className={styles.bonusHint}>Net mineral acres · —</span>
            </>
          )}
        </div>
      </fieldset>
    </div>
  );
}

/** Props for one {@link ElectionGroupCard}. */
interface ElectionGroupCardProps {
  tractId: string;
  group: ElectionGroup;
  /** Every group on the tract — a property already in a sibling is off the menu. */
  groups: ElectionGroup[];
  wells: CarryWell[];
  /** False when this is the last group (removal is blocked — ≥ 1 group invariant). */
  canRemove: boolean;
  renameElectionGroup: WorkingConfigForm['renameElectionGroup'];
  removeElectionGroup: WorkingConfigForm['removeElectionGroup'];
  setElectionGroupWells: WorkingConfigForm['setElectionGroupWells'];
  patchElectionGroupTerms: WorkingConfigForm['patchElectionGroupTerms'];
}

/**
 * One election property group: name (inline rename), remove control, the shared
 * property picker, and our participating interest in the properties it holds.
 *
 * The participating pair is the ONE thing a group owns besides its properties —
 * everything else about a UMI election (royalty rates, trigger, bonus) is negotiated
 * for the tract. It is re-seeded from the properties on every accepted selection, so
 * the terms always state the interest the group actually holds.
 *
 * @param {ElectionGroupCardProps} props - The group + its tract, roster, and the setters.
 * @return {JSX.Element} The group card.
 */
function ElectionGroupCard({
  tractId,
  group,
  groups,
  wells,
  canRemove,
  renameElectionGroup,
  removeElectionGroup,
  setElectionGroupWells,
  patchElectionGroupTerms,
}: ElectionGroupCardProps) {
  /**
   * Apply an accepted selection. The picker has already guaranteed the set is
   * homogeneous, so these terms are our real interest in every property the group
   * now holds. Absent only when the properties carry no certified figure, in which
   * case the existing terms stand rather than being zeroed.
   *
   * @param {CarryWell[]} next - The properties the group now holds.
   * @return {void}
   */
  const applyWells = (next: CarryWell[]): void => {
    setElectionGroupWells(
      tractId,
      group.id,
      next.map((well) => well.id)
    );
    const terms = carryTermsFromWells(next);
    if (terms) {
      patchElectionGroupTerms(tractId, group.id, {
        ownerWi: terms.apoWi,
        ownerNri: terms.apoNri,
      });
    }
  };

  return (
    <div className={styles.groupCard}>
      <div className={styles.groupCardHead}>
        <TextInput
          id={`${group.id}-name`}
          labelText="Group name"
          hideLabel
          size="sm"
          value={group.name}
          onChange={(event) =>
            renameElectionGroup(tractId, group.id, event.target.value)
          }
        />
        {group.electedElection && (
          <Tag type="teal" size="sm">
            {ELECTION_VARIANT_BY_ID[group.electedElection].label}
          </Tag>
        )}
        <Button
          hasIconOnly
          // Carbon routes a hasIconOnly Button through IconButton, whose `kind`
          // does not accept the `danger--*` variants — the TrashCan icon + label
          // carry the destructive intent instead.
          kind="ghost"
          size="sm"
          renderIcon={TrashCan}
          iconDescription="Remove group"
          tooltipPosition="left"
          disabled={!canRemove}
          onClick={() => removeElectionGroup(tractId, group.id)}
        />
      </div>

      <PropertyPicker
        group={group}
        groups={groups}
        wells={wells}
        onChange={applyWells}
      />

      {/* The same before/after-payout shape the OGL carry editor uses, because a
        carried election IS a carry: before payout we hold whatever the BPO pair
        says (0/0 by default — carried means we hold nothing until payout), and at
        payout we convert to our participating interest. All four boxes are live and
        stated on ONE basis: a percent of the well. */}
      <div className={styles.termsGrid}>
        <div className={styles.termsCol}>
          <span className={styles.sectionLabel}>Before payout (BPO)</span>
          <PctInput
            id={`${group.id}-bpowi`}
            label="WI %"
            ariaLabel="Before payout WI %"
            value={group.bpoWi ?? null}
            onChange={(value) =>
              patchElectionGroupTerms(tractId, group.id, { bpoWi: value })
            }
          />
          <PctInput
            id={`${group.id}-bponri`}
            label="NRI %"
            ariaLabel="Before payout NRI %"
            value={group.bpoNri ?? null}
            onChange={(value) =>
              patchElectionGroupTerms(tractId, group.id, { bpoNri: value })
            }
          />
        </div>
        <div className={styles.termsCol}>
          <span className={styles.sectionLabel}>After payout (APO)</span>
          <PctInput
            id={`${group.id}-apowi`}
            label="WI %"
            ariaLabel="After payout WI %"
            value={group.ownerWi}
            onChange={(value) =>
              patchElectionGroupTerms(tractId, group.id, { ownerWi: value })
            }
          />
          <PctInput
            id={`${group.id}-aponri`}
            label="NRI %"
            ariaLabel="After payout NRI %"
            value={group.ownerNri}
            onChange={(value) =>
              patchElectionGroupTerms(tractId, group.id, { ownerNri: value })
            }
          />
        </div>
      </div>
    </div>
  );
}
